import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, X, ChevronsUpDown, Check, Search, RefreshCw } from 'lucide-react';

const REJECTION_REASONS = [
  'Startup', 'ShortFill', 'Undercut', 'SilverStreaks', 'Burn Mark',
  'Flowline', 'Shinemark', 'Weldline', 'Scratches', 'Bubbles',
  'Sink mark', 'Color Variation', 'Others',
];

const PartInspection = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [productions, setProductions] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [partOpen, setPartOpen] = useState(false);
  const [productionOpen, setProductionOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [inspectionDetails, setInspectionDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingInspection, setViewingInspection] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'QUALITY', 'PRODUCTION']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/part-inspections');
      setData(res.data);
    } catch (e) { toast.error('Failed to fetch inspections'); }
    finally { setLoading(false); }
    try { const r = await api.get('/employees'); setEmployees(r.data); } catch (e) {}
    try {
      const r = await api.get('/productions/for-inspection');
      console.log('[PartInspection] productions loaded:', r.data);
      setProductions(r.data);
    } catch (e) { toast.error('Failed to load productions for inspection'); }
    try {
      const r = await api.get('/parts');
      console.log('[PartInspection] parts loaded:', r.data?.length, r.data);
      setParts(r.data);
    } catch (e) { toast.error('Failed to load parts'); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getPartName = (id) => {
    const p = parts.find(x => x.id === parseInt(id));
    return p ? p.name : '-';
  };
  const getPartNo = (id) => {
    const p = parts.find(x => x.id === parseInt(id));
    return p ? p.part_no : '-';
  };
  const getEmpLabel = (id) => {
    const e = employees.find(x => x.id === parseInt(id));
    return e ? `${e.employee_code} - ${e.first_name} ${e.last_name || ''}` : '-';
  };

  // prod is an item from productions array: { id, source, production_no, run_no, production_date, details }
  const getProdOptionLabel = (prod) => {
    if (!prod) return '-';
    if (prod.source === 'LEGACY') return `P-${prod.production_no} (${new Date(prod.production_date).toLocaleDateString()})`;
    return `RUN-${prod.run_no} (${new Date(prod.production_date).toLocaleDateString()})`;
  };

  // prodKey = "LEGACY:id" or "RUN:id"
  const getProdLabel = (prodKey) => {
    if (!prodKey) return '-';
    const [source, idStr] = prodKey.split(':');
    const prod = productions.find(p => p.source === source && p.id === parseInt(idStr));
    return prod ? getProdOptionLabel(prod) : prodKey;
  };

  // Get label for a saved inspection record (has production_id or production_run_id)
  const getProdLabelForItem = (item) => {
    if (item.production_run_id) {
      const prod = productions.find(p => p.source === 'RUN' && p.id === item.production_run_id);
      return prod ? getProdOptionLabel(prod) : `RUN-${item.production_run_id}`;
    }
    if (item.production_id) {
      const prod = productions.find(p => p.source === 'LEGACY' && p.id === item.production_id);
      return prod ? getProdOptionLabel(prod) : `P-${item.production_id}`;
    }
    return '-';
  };

  // Build inspectedMap: composite key → total QC-inspected qty
  // Key: "LEGACY:production_detail_id" or "RUN:production_run_output_id"
  const inspectedMap = {};
  data.forEach(inspection => {
    (inspection.details || []).forEach(d => {
      let key;
      if (d.production_run_output_id) {
        key = `RUN:${d.production_run_output_id}`;
      } else if (d.production_detail_id) {
        key = `LEGACY:${d.production_detail_id}`;
      } else {
        return;
      }
      inspectedMap[key] = (inspectedMap[key] || 0) + (parseFloat(d.accepted_quantity) || 0) + (parseFloat(d.rejected_quantity) || 0);
    });
  });

  // Productions filtered by selected part
  const filteredProductions = formData.filter_part_id
    ? productions.filter(p =>
        p.details && p.details.some(d => d.part_id === parseInt(formData.filter_part_id))
      )
    : [];

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    return String(item.inspection_no).includes(searchTerm.toLowerCase());
  });

  const handlePartChange = (partId) => {
    console.log('[PartInspection] part selected:', partId, '| productions:', productions.length);
    productions.forEach(p => {
      const match = p.details && p.details.some(d => d.part_id === parseInt(partId));
      console.log(`  ${p.source} id=${p.id} run_no=${p.run_no} details part_ids=[${(p.details||[]).map(d=>d.part_id).join(',')}] match=${match}`);
    });
    setFormData(prev => ({ ...prev, filter_part_id: partId, production_key: '' }));
    setInspectionDetails([]);
    setPartOpen(false);
  };

  const handleProductionChange = (prodKey) => {
    const [source, idStr] = prodKey.split(':');
    const prod = productions.find(p => p.source === source && p.id === parseInt(idStr));
    setFormData(prev => ({ ...prev, production_key: prodKey }));
    if (prod && formData.filter_part_id) {
      const partDetail = prod.details.find(d => d.part_id === parseInt(formData.filter_part_id));
      if (partDetail) {
        const detailKey = `${source}:${partDetail.id}`;
        const netProduced = (parseFloat(partDetail.produced_quantity) || 0) - (parseFloat(partDetail.rejected_quantity) || 0);
        const remaining = Math.max(0, netProduced - (inspectedMap[detailKey] || 0));
        setInspectionDetails([{
          source,
          detail_ref_id: partDetail.id,
          part_id: formData.filter_part_id,
          available_quantity: remaining,
          taken_for_qc: remaining,
          accepted_quantity: remaining,
          rejected_quantity: 0,
          rejection_details: [],
        }]);
      }
    }
    setProductionOpen(false);
  };

  const handleDetailChange = (index, field, value) => {
    const updated = [...inspectionDetails];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'taken_for_qc' || field === 'rejected_quantity') {
      const taken = parseFloat(field === 'taken_for_qc' ? value : updated[index].taken_for_qc) || 0;
      const rejected = parseFloat(field === 'rejected_quantity' ? value : updated[index].rejected_quantity) || 0;
      updated[index].accepted_quantity = Math.max(0, taken - rejected);
    }
    setInspectionDetails(updated);
  };

  const handleAddRejection = (dIdx) => {
    const updated = [...inspectionDetails];
    updated[dIdx].rejection_details = [...(updated[dIdx].rejection_details || []), { rejection_reason: '', quantity: 0 }];
    setInspectionDetails(updated);
  };

  const handleRejectionChange = (dIdx, rIdx, field, value) => {
    const updated = [...inspectionDetails];
    updated[dIdx].rejection_details[rIdx] = { ...updated[dIdx].rejection_details[rIdx], [field]: value };
    setInspectionDetails(updated);
  };

  const handleRemoveRejection = (dIdx, rIdx) => {
    const updated = [...inspectionDetails];
    updated[dIdx].rejection_details = updated[dIdx].rejection_details.filter((_, i) => i !== rIdx);
    setInspectionDetails(updated);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      const firstDetail = item.details && item.details[0];
      const prodKey = item.production_run_id
        ? `RUN:${item.production_run_id}`
        : item.production_id
          ? `LEGACY:${item.production_id}`
          : '';
      setFormData({
        inspection_date: item.inspection_date || '',
        filter_part_id: firstDetail ? firstDetail.part_id.toString() : '',
        production_key: prodKey,
        inspector_id: item.inspector_id ? item.inspector_id.toString() : '',
        remarks: item.remarks || '',
      });
      setInspectionDetails((item.details || []).map(d => ({
        source: d.production_run_output_id ? 'RUN' : 'LEGACY',
        detail_ref_id: d.production_run_output_id || d.production_detail_id,
        part_id: d.part_id ? d.part_id.toString() : '',
        available_quantity: d.produced_quantity || 0,
        taken_for_qc: d.produced_quantity || 0,
        accepted_quantity: d.accepted_quantity || 0,
        rejected_quantity: d.rejected_quantity || 0,
        rejection_details: d.rejection_details || [],
      })));
    } else {
      setEditingItem(null);
      setFormData({
        inspection_date: new Date().toISOString().split('T')[0],
        filter_part_id: '',
        production_key: '',
        inspector_id: '',
        remarks: '',
      });
      setInspectionDetails([]);
    }
    setPartOpen(false);
    setProductionOpen(false);
    setInspectorOpen(false);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.filter_part_id || !formData.production_key || !formData.inspector_id || !formData.inspection_date) {
      toast.error('Date, Part, Production and Inspector are required');
      return;
    }
    for (const detail of inspectionDetails) {
      const taken = parseFloat(detail.taken_for_qc) || 0;
      const rejected = parseFloat(detail.rejected_quantity) || 0;
      if (taken <= 0) {
        toast.error(`Taken for QC must be greater than 0 for ${getPartName(parseInt(detail.part_id))}`);
        return;
      }
      if (rejected > taken) {
        toast.error(`Rejected (${rejected}) cannot exceed Taken for QC (${taken}) for ${getPartName(parseInt(detail.part_id))}`);
        return;
      }
      if (rejected > 0) {
        const valid = (detail.rejection_details || []).filter(r => r.rejection_reason && r.quantity);
        if (valid.length === 0) {
          toast.error(`Rejection reasons required for ${getPartName(parseInt(detail.part_id))}`);
          return;
        }
        const sum = valid.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
        if (Math.abs(sum - rejected) > 0.01) {
          toast.error(`Rejection reasons total (${sum}) must match rejected qty (${rejected}) for ${getPartName(parseInt(detail.part_id))}`);
          return;
        }
      }
    }

    const [source, idStr] = (formData.production_key || '').split(':');
    const sourceId = parseInt(idStr);

    setIsSubmitting(true);
    try {
      const submitData = {
        inspection_date: formData.inspection_date,
        production_id: source === 'LEGACY' ? sourceId : null,
        production_run_id: source === 'RUN' ? sourceId : null,
        inspector_id: parseInt(formData.inspector_id),
        remarks: formData.remarks || null,
        details: inspectionDetails.filter(d => d.part_id).map(d => ({
          production_detail_id: d.source === 'LEGACY' ? d.detail_ref_id : null,
          production_run_output_id: d.source === 'RUN' ? d.detail_ref_id : null,
          part_id: parseInt(d.part_id),
          produced_quantity: parseFloat(d.taken_for_qc) || 0,
          accepted_quantity: parseFloat(d.accepted_quantity) || 0,
          rejected_quantity: parseFloat(d.rejected_quantity) || 0,
          rejection_details: (d.rejection_details || [])
            .filter(r => r.rejection_reason && r.quantity > 0)
            .map(r => ({ rejection_reason: r.rejection_reason, quantity: parseFloat(r.quantity) || 0 })),
        })),
      };
      if (editingItem) {
        await api.put(`/part-inspections/${editingItem.id}`, submitData);
        toast.success('Inspection updated');
      } else {
        await api.post('/part-inspections', submitData);
        toast.success('Inspection created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const msg = (error.response && error.response.data && error.response.data.detail) || 'Operation failed';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleView = async (item) => {
    try {
      const res = await api.get(`/part-inspections/${item.id}`);
      setViewingInspection(res.data);
      setIsViewDialogOpen(true);
    } catch (e) { toast.error('Failed to load inspection details'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this inspection?')) return;
    try {
      await api.delete(`/part-inspections/${item.id}`);
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error('Delete failed'); }
  };

  const getTotals = (details) => (details || []).reduce(
    (acc, d) => ({ accepted: acc.accepted + (parseFloat(d.accepted_quantity) || 0), rejected: acc.rejected + (parseFloat(d.rejected_quantity) || 0) }),
    { accepted: 0, rejected: 0 }
  );

  // Pending items across all production sources
  const pendingItems = [];
  productions.forEach(prod => {
    (prod.details || []).forEach(d => {
      const detailKey = `${prod.source}:${d.id}`;
      const netProduced = (parseFloat(d.produced_quantity) || 0) - (parseFloat(d.rejected_quantity) || 0);
      const totalInspected = inspectedMap[detailKey] || 0;
      const pending = netProduced - totalInspected;
      if (pending > 0) {
        pendingItems.push({
          source: prod.source,
          production_id: prod.id,
          production_no: prod.production_no,
          run_no: prod.run_no,
          production_date: prod.production_date,
          part_id: d.part_id,
          produced_quantity: parseFloat(d.produced_quantity) || 0,
          op_rejected: parseFloat(d.rejected_quantity) || 0,
          net_produced: netProduced,
          total_inspected: totalInspected,
          pending,
        });
      }
    });
  });

  return (
    <div className="p-6 max-w-[1400px] mx-auto animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Part Inspection</h1>
          <p className="page-subtitle">Quality check of produced parts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="rounded-sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          {canEdit && (
            <Button size="sm" onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              <Plus className="w-4 h-4 mr-1" /> New Inspection
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by QC number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 rounded-sm" />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No inspections found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>QC No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Production</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Part</TableHead>
                    <TableHead className="text-right">Accepted</TableHead>
                    <TableHead className="text-right">Rejected</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.flatMap((item) => {
                    const activeParts = (item.details || []).filter(d => parseFloat(d.produced_quantity) > 0);
                    const rows = activeParts.length > 0 ? activeParts : [null];
                    const numRows = rows.length;
                    const totals = getTotals(item.details);
                    return rows.map((d, i) => (
                      <TableRow key={`${item.id}-${i}`} className={i > 0 ? 'border-t border-slate-100' : ''}>
                        {i === 0 && <>
                          <TableCell className="font-mono font-bold align-top" rowSpan={numRows}>QC-{item.inspection_no}</TableCell>
                          <TableCell className="align-top" rowSpan={numRows}>{new Date(item.inspection_date).toLocaleDateString()}</TableCell>
                          <TableCell className="font-mono text-sm align-top" rowSpan={numRows}>{getProdLabelForItem(item)}</TableCell>
                          <TableCell className="text-sm align-top" rowSpan={numRows}>{getEmpLabel(item.inspector_id)}</TableCell>
                        </>}
                        <TableCell className="text-sm">{d ? getPartName(d.part_id) : '-'}</TableCell>
                        {i === 0 && <>
                          <TableCell className="text-right font-mono text-green-600 align-top" rowSpan={numRows}>{totals.accepted.toFixed(0)}</TableCell>
                          <TableCell className="text-right font-mono text-red-600 align-top" rowSpan={numRows}>{totals.rejected.toFixed(0)}</TableCell>
                          <TableCell className="text-right align-top" rowSpan={numRows}>
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8"><Eye className="w-4 h-4" /></Button>
                              {canEdit && <>
                                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600"><Trash2 className="w-4 h-4" /></Button>
                              </>}
                            </div>
                          </TableCell>
                        </>}
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Inspection */}
      {!loading && pendingItems.length > 0 && (
        <Card className="mt-4 border-amber-200">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200 bg-amber-50">
              <span className="text-sm font-semibold text-amber-800">
                Pending QC Inspection — {pendingItems.length} part{pendingItems.length !== 1 ? 's' : ''} awaiting inspection
              </span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50">
                    <TableHead>Production</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Part</TableHead>
                    <TableHead className="text-right">Produced</TableHead>
                    <TableHead className="text-right">Op. Rejected</TableHead>
                    <TableHead className="text-right">Net Available</TableHead>
                    <TableHead className="text-right">QC Inspected</TableHead>
                    <TableHead className="text-right text-amber-700">Pending</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingItems.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono font-bold">
                        {row.source === 'LEGACY' ? `P-${row.production_no}` : `RUN-${row.run_no}`}
                      </TableCell>
                      <TableCell>{new Date(row.production_date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm">{getPartName(row.part_id)}</TableCell>
                      <TableCell className="text-right font-mono">{row.produced_quantity.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{row.op_rejected.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-mono">{row.net_produced.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-mono text-green-600">{row.total_inspected.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-amber-700">{row.pending.toFixed(0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit QC-${editingItem.inspection_no}` : 'New Part Inspection'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
              <div>
                <Label className="form-label">Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.inspection_date} onChange={(e) => setFormData(prev => ({ ...prev, inspection_date: e.target.value }))} required className="rounded-sm" />
              </div>

              {/* 1. Select Part first */}
              <div>
                <Label className="form-label">Part <span className="text-red-500">*</span></Label>
                <Popover open={partOpen} onOpenChange={setPartOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" aria-expanded={partOpen}
                      className="w-full justify-between rounded-sm font-normal text-sm h-9"
                      disabled={!!editingItem}>
                      <span className="truncate">
                        {formData.filter_part_id ? `${getPartNo(formData.filter_part_id)} - ${getPartName(formData.filter_part_id)}` : 'Select Part'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                    <Command>
                      <CommandInput placeholder="Search part..." />
                      <CommandList>
                        <CommandEmpty>No parts found.</CommandEmpty>
                        <CommandGroup>
                          {parts.map((part) => (
                            <CommandItem key={part.id} value={`${part.part_no} ${part.name}`}
                              onSelect={() => handlePartChange(part.id.toString())}>
                              <Check className={formData.filter_part_id === part.id.toString() ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0'} />
                              {part.part_no} - {part.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* 2. Then select Production (filtered by part) */}
              <div>
                <Label className="form-label">Production Run <span className="text-red-500">*</span></Label>
                <Popover open={productionOpen} onOpenChange={setProductionOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" aria-expanded={productionOpen}
                      className="w-full justify-between rounded-sm font-normal text-sm h-9"
                      disabled={!!editingItem || !formData.filter_part_id}>
                      <span className="truncate">
                        {formData.production_key
                          ? getProdLabel(formData.production_key)
                          : (formData.filter_part_id ? 'Select Production Run' : 'Select Part first')}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                    <Command>
                      <CommandInput placeholder="Search production..." />
                      <CommandList>
                        <CommandEmpty>
                          {productions.length === 0
                            ? 'No data loaded — restart server & refresh page'
                            : `No runs contain this part (${productions.length} run(s) loaded)`}
                        </CommandEmpty>
                        <CommandGroup>
                          {filteredProductions.map((prod) => {
                            const prodKey = `${prod.source}:${prod.id}`;
                            const label = getProdOptionLabel(prod);
                            return (
                              <CommandItem key={prodKey} value={label}
                                onSelect={() => handleProductionChange(prodKey)}>
                                <Check className={formData.production_key === prodKey ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0'} />
                                {label}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="form-label">Inspector <span className="text-red-500">*</span></Label>
                <Popover open={inspectorOpen} onOpenChange={setInspectorOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" aria-expanded={inspectorOpen}
                      className="w-full justify-between rounded-sm font-normal text-sm h-9">
                      <span className="truncate">{formData.inspector_id ? getEmpLabel(formData.inspector_id) : 'Select Inspector'}</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                    <Command>
                      <CommandInput placeholder="Search employee..." />
                      <CommandList>
                        <CommandEmpty>No employees found.</CommandEmpty>
                        <CommandGroup>
                          {employees.filter(e => e.is_active).map((emp) => (
                            <CommandItem key={emp.id}
                              value={`${emp.employee_code} ${emp.first_name} ${emp.last_name || ''}`}
                              onSelect={() => { setFormData(prev => ({ ...prev, inspector_id: emp.id.toString() })); setInspectorOpen(false); }}>
                              <Check className={formData.inspector_id === emp.id.toString() ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0'} />
                              {emp.employee_code} - {emp.first_name} {emp.last_name || ''}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="form-label">Remarks</Label>
                <Input value={formData.remarks} onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))} className="rounded-sm" />
              </div>
            </div>

            {inspectionDetails.length > 0 && (
              <div className="mt-2 border-t pt-4">
                <h3 className="font-semibold text-slate-900 mb-3">Part Quality Details</h3>
                <div className="space-y-3">
                  {inspectionDetails.map((detail, index) => {
                    const rejected = parseFloat(detail.rejected_quantity) || 0;
                    const rejSum = (detail.rejection_details || []).reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
                    const rejMismatch = rejected > 0 && Math.abs(rejSum - rejected) > 0.01;
                    return (
                      <div key={index} className="border rounded-sm p-3">
                        <div className="mb-2">
                          <span className="font-medium text-sm">
                            {getPartName(parseInt(detail.part_id))} ({getPartNo(parseInt(detail.part_id))})
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs text-slate-500">Available for QC</Label>
                            <Input type="number" value={detail.available_quantity} readOnly className="rounded-sm bg-slate-50 text-slate-600" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Taken for QC</Label>
                            <Input type="number" value={detail.taken_for_qc}
                              onChange={(e) => handleDetailChange(index, 'taken_for_qc', e.target.value)}
                              className="rounded-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Rejected</Label>
                            <Input type="number" value={detail.rejected_quantity}
                              onChange={(e) => handleDetailChange(index, 'rejected_quantity', e.target.value)}
                              className={`rounded-sm ${rejected > 0 ? 'border-red-300' : ''}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Accepted</Label>
                            <Input type="number" value={detail.accepted_quantity} readOnly className="rounded-sm bg-slate-50 text-green-700 font-medium" />
                          </div>
                        </div>
                        {rejected > 0 && (
                          <div className="mt-3 pl-3 border-l-2 border-red-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-red-700">
                                Rejection Reasons (Total: {rejSum}/{rejected})
                                {rejMismatch && <span className="ml-2 text-red-500">Mismatch!</span>}
                              </span>
                              <Button type="button" variant="outline" size="sm" onClick={() => handleAddRejection(index)} className="h-6 text-xs">
                                <Plus className="w-3 h-3 mr-1" /> Reason
                              </Button>
                            </div>
                            {(detail.rejection_details || []).map((rej, rIdx) => (
                              <div key={rIdx} className="flex items-center gap-2 mb-1">
                                <Select value={rej.rejection_reason || undefined} onValueChange={(v) => handleRejectionChange(index, rIdx, 'rejection_reason', v)}>
                                  <SelectTrigger className="rounded-sm h-8 text-xs w-40"><SelectValue placeholder="Reason" /></SelectTrigger>
                                  <SelectContent>
                                    {REJECTION_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Input type="number" value={rej.quantity}
                                  onChange={(e) => handleRejectionChange(index, rIdx, 'quantity', e.target.value)}
                                  className="rounded-sm h-8 w-20 text-xs" placeholder="Qty" />
                                <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveRejection(index, rIdx)} className="h-6 w-6 text-red-500">
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            {(detail.rejection_details || []).length === 0 && (
                              <p className="text-xs text-red-500">Add rejection reasons (mandatory when rejected qty &gt; 0)</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!editingItem && inspectionDetails.length === 0 && (
              <div className="mt-4 text-center py-6 text-slate-500 border border-dashed rounded-sm">
                {!formData.filter_part_id ? 'Select a part to begin.' : !formData.production_key ? 'Select a production run.' : 'No produced quantity found for this part in the selected run.'}
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>QC-{viewingInspection ? viewingInspection.inspection_no : ''}</DialogTitle>
          </DialogHeader>
          {viewingInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><span className="text-sm text-slate-500">Date</span><p className="font-medium">{new Date(viewingInspection.inspection_date).toLocaleDateString()}</p></div>
                <div><span className="text-sm text-slate-500">Production</span><p className="font-medium font-mono">{getProdLabelForItem(viewingInspection)}</p></div>
                <div><span className="text-sm text-slate-500">Inspector</span><p className="font-medium">{getEmpLabel(viewingInspection.inspector_id)}</p></div>
                {viewingInspection.remarks && (
                  <div className="col-span-3"><span className="text-sm text-slate-500">Remarks</span><p className="font-medium">{viewingInspection.remarks}</p></div>
                )}
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">
                  Parts ({(viewingInspection.details || []).filter(d => parseFloat(d.produced_quantity) > 0).length})
                </h4>
                {(viewingInspection.details || []).filter(d => parseFloat(d.produced_quantity) > 0).map((d, idx) => (
                  <div key={idx} className="border rounded-sm p-3 mb-2">
                    <div className="mb-1">
                      <span className="font-medium">{getPartName(d.part_id)} ({getPartNo(d.part_id)})</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>Produced: <span className="font-mono">{parseFloat(d.produced_quantity).toFixed(0)}</span></div>
                      <div className="text-green-600">Accepted: <span className="font-mono">{parseFloat(d.accepted_quantity).toFixed(0)}</span></div>
                      <div className="text-red-600">Rejected: <span className="font-mono">{parseFloat(d.rejected_quantity).toFixed(0)}</span></div>
                    </div>
                    {d.rejection_details && d.rejection_details.length > 0 && (
                      <div className="mt-2 pl-3 border-l-2 border-red-200">
                        {d.rejection_details.map((r, ri) => (
                          <div key={ri} className="text-xs text-red-700">{r.rejection_reason}: {parseFloat(r.quantity).toFixed(0)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartInspection;
